import { supabase } from '@/integrations/supabase/client';
import { invokeWithRetry } from './common/invokeWithRetry';

/**
 * Feature flags for profiles management edge function migration
 * Set to true to use edge function, false to use direct database calls (fallback)
 */
const FEATURE_FLAGS = {
  useGetUserProfile: true,
  useGetUserProfilesBulk: true,
  useUpdateUserProfile: true,
};

/**
 * Get single user profile
 */
export async function getUserProfile(userId: string) {
  if (FEATURE_FLAGS.useGetUserProfile) {
    console.log('[profilesWrapper] Using edge function for getUserProfile');
    const { data, error, authExpired } = await invokeWithRetry('profiles-management', {
      action: 'get_user_profile',
      user_id: userId,
    });

    if (authExpired) {
      return { data: null, error: null };
    }

    if (error) throw error;
    if (!data.success) throw new Error(data.error || 'Failed to fetch profile');
    return { data: data.data, error: null };
  }

  // Fallback: direct database call
  console.log('[profilesWrapper] Using direct DB for getUserProfile (fallback)');
  return await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();
}

/**
 * Get multiple user profiles in bulk (N+1 optimization)
 */
export async function getUserProfilesBulk(userIds: string[]) {
  // Guard: retourne tableau vide si aucun ID fourni
  if (!userIds || userIds.length === 0) {
    console.log('[profilesWrapper] getUserProfilesBulk called with empty array, returning []');
    return { data: [], error: null };
  }

  if (FEATURE_FLAGS.useGetUserProfilesBulk) {
    console.log('[profilesWrapper] Using edge function for getUserProfilesBulk');
    const { data, error, authExpired } = await invokeWithRetry('profiles-management', {
      action: 'get_user_profiles_bulk',
      user_ids: userIds,
    });

    if (authExpired) {
      return { data: [], error: null };
    }

    if (error) throw error;
    if (!data.success) throw new Error(data.error || 'Failed to fetch profiles');
    return { data: data.data, error: null };
  }

  // Fallback: direct database call
  console.log('[profilesWrapper] Using direct DB for getUserProfilesBulk (fallback)');
  return await supabase
    .from('user_profiles')
    .select('*')
    .in('id', userIds);
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  userId: string, 
  profileData: Record<string, any>,
  gymId?: string
) {
  if (FEATURE_FLAGS.useUpdateUserProfile) {
    console.log('[profilesWrapper] Using edge function for updateUserProfile');
    const { data, error, authExpired } = await invokeWithRetry('profiles-management', {
      action: 'update_user_profile',
      user_id: userId,
      profile_data: profileData,
      gym_id: gymId,
    });

    if (authExpired) {
      return { data: null, error: null };
    }

    if (error) throw error;
    if (!data.success) throw new Error(data.error || 'Failed to update profile');
    return { data: data.data, error: null };
  }

  // Fallback: direct database call
  console.log('[profilesWrapper] Using direct DB for updateUserProfile (fallback)');
  return await supabase
    .from('user_profiles')
    .update(profileData as never)
    .eq('id', userId)
    .select()
    .single();
}
